import React from 'react';
import { Button } from './ui/button';
import { Star, Award, Play } from 'lucide-react';

const Hero = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden pt-12 bg-white">
      {/* Hero Image - Right side */}
      <div className="absolute right-0 top-0 bottom-0 w-1/2 lg:w-[60%] flex items-center justify-center z-0 pr-8">
        <img 
          src="https://customer-assets.emergentagent.com/job_058bccbf-ae46-496f-9a9e-541820fd866a/artifacts/r31323t7_ChatGPT%20Image%20Mar%202%2C%202026%2C%2010_26_06%20AM.png"
          alt="Home Cinema Speaker System"
          className="h-auto w-full max-w-3xl object-contain drop-shadow-2xl"
          style={{ 
            imageRendering: 'crisp-edges',
            filter: 'contrast(1.05) saturate(1.1)'
          }}
          loading="eager"
        />
      </div>
      
      {/* Gradient overlay for blending */}
      <div className="absolute inset-0 bg-gradient-to-r from-white from-30% via-white/90 via-45% to-transparent to-60% z-[1]"></div>

      <div className="container mx-auto px-6 relative z-10 py-12">
        <div className="max-w-2xl">
          {/* Main Heading */}
          <div className="mb-8 mt-12">
            <h1 className="text-xl md:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight mb-4">
              EXPERIENCE
              <br />
              CINEMA PERFECTION
              <br />
              <span className="text-cyan-800">AT HOME</span>
            </h1>
            <p className="text-base md:text-lg text-gray-700 max-w-xl">
              Transform your space with premium{' '}
              <span className="text-cyan-800 font-semibold">Dolby Atmos theatres</span>, 4K projection,
              and custom acoustic design. Experience movies like never before.
            </p>
          </div>

          {/* Info Cards */}
          <div className="flex items-center gap-4 mb-8">
            <div className="flex-1 max-w-xs px-6 py-3 rounded-xl bg-cyan-50 border-2 border-cyan-700/30 backdrop-blur-sm">
              <div className="text-cyan-800 text-2xl font-bold">₹2.29L+</div>
              <div className="text-gray-700 text-xs uppercase tracking-wide font-medium">THEATRE STARTS</div>
            </div>
            <div className="flex-1 max-w-xs px-6 py-3 rounded-xl bg-cyan-50 border-2 border-cyan-700/30 backdrop-blur-sm">
              <div className="text-cyan-800 text-2xl font-bold">200+</div>
              <div className="text-gray-700 text-xs uppercase tracking-wide font-medium">THEATRES BUILT</div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-wrap items-center gap-4 mb-12">
            <Button
              onClick={() => scrollToSection('packages')}
              className="bg-cyan-800 hover:bg-cyan-900 text-white font-semibold px-8 py-3 text-base rounded-full transition-all duration-200 shadow-lg shadow-cyan-800/30"
            >
              VIEW PACKAGES
              <span className="ml-2">→</span>
            </Button>
            <Button
              onClick={() => window.open('https://wa.me/917204247443', '_blank')}
              variant="outline"
              className="border-2 border-gray-300 bg-white/80 backdrop-blur-sm text-gray-900 hover:bg-white px-8 py-3 text-base font-semibold rounded-full transition-all duration-200"
            >
              <Play className="w-4 h-4 mr-2" />
              BOOK DEMO
            </Button>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap items-center gap-8 md:gap-16">
            <div>
              <div className="text-3xl md:text-4xl font-bold text-cyan-800 mb-1">100+</div>
              <div className="text-xs md:text-sm text-gray-600 uppercase tracking-wide leading-tight">
                PREMIUM
                <br />
                INSTALLATIONS
              </div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-cyan-800 mb-1">8+</div>
              <div className="text-xs md:text-sm text-gray-600 uppercase tracking-wide leading-tight">
                YEARS OF
                <br />
                EXPERTISE
              </div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-cyan-800 mb-1">200+</div>
              <div className="text-xs md:text-sm text-gray-600 uppercase tracking-wide leading-tight">
                HOME THEATRES
                <br />
                DELIVERED
              </div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-cyan-800 mb-1">5/5</div>
              <div className="text-xs md:text-sm text-gray-600 uppercase tracking-wide leading-tight">
                CUSTOMER
                <br />
                RATINGS
              </div>
            </div>
          </div>
          
          {/* Tagline Badge */}
          <div className="mt-8">
            <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-cyan-50/90 backdrop-blur-sm border-2 border-cyan-700/30">
              <Award className="w-4 h-4 text-cyan-800" />
              <span className="text-cyan-800 font-medium text-sm">Bangalore's #1 Home Theatre Specialists</span>
              <div className="flex items-center gap-0.5 ml-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                ))}
                <span className="text-gray-700 ml-1 text-sm font-medium">5/5</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
